import { Trophy, Zap, Target, Flame, Star, Crown, Rocket, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  unlocked: boolean;
  progress?: number;
  maxProgress?: number;
}

const achievements: Achievement[] = [
  { id: "first_trade", name: "First Blood", description: "Complete your first trade", icon: Zap, color: "text-primary", unlocked: true },
  { id: "trader_10", name: "Active Trader", description: "Complete 10 trades", icon: Target, color: "text-success", unlocked: true, progress: 10, maxProgress: 10 },
  { id: "trader_100", name: "Degen Mode", description: "Complete 100 trades", icon: Flame, color: "text-orange-500", unlocked: false, progress: 23, maxProgress: 100 },
  { id: "profit_1k", name: "Profit Hunter", description: "Earn $1,000 in profits", icon: Trophy, color: "text-yellow-500", unlocked: false, progress: 450, maxProgress: 1000 },
  { id: "profit_10k", name: "Whale Status", description: "Earn $10,000 in profits", icon: Crown, color: "text-purple-500", unlocked: false, progress: 450, maxProgress: 10000 },
  { id: "streak_7", name: "On Fire", description: "7-day trading streak", icon: Flame, color: "text-red-500", unlocked: false, progress: 3, maxProgress: 7 },
  { id: "early_bird", name: "Early Bird", description: "Trade a coin within 1 minute of launch", icon: Rocket, color: "text-cyan-500", unlocked: true },
  { id: "diamond_hands", name: "Diamond Hands", description: "Hold a position for 30 days", icon: Shield, color: "text-blue-500", unlocked: false, progress: 12, maxProgress: 30 },
];

export function Achievements() {
  const unlockedCount = achievements.filter(a => a.unlocked).length;

  return (
    <div className="p-5 rounded-xl bg-card border border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Achievements</h3>
        <span className="text-sm text-muted-foreground">{unlockedCount}/{achievements.length}</span>
      </div>
      
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
        {achievements.map((achievement) => {
          const Icon = achievement.icon;
          const progressPercent = achievement.maxProgress 
            ? Math.min(100, ((achievement.progress ?? 0) / achievement.maxProgress) * 100)
            : 0;
          
          return (
            <Tooltip key={achievement.id}>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    "relative w-12 h-12 rounded-xl flex items-center justify-center cursor-pointer transition-all",
                    achievement.unlocked 
                      ? "bg-secondary hover:scale-110" 
                      : "bg-secondary/30 grayscale"
                  )}
                >
                  <Icon className={cn("w-6 h-6", achievement.unlocked ? achievement.color : "text-muted-foreground/50")} />
                  {!achievement.unlocked && achievement.progress !== undefined && (
                    <div className="absolute inset-0 rounded-xl overflow-hidden">
                      <div 
                        className="absolute bottom-0 left-0 right-0 bg-primary/20"
                        style={{ height: `${progressPercent}%` }}
                      />
                    </div>
                  )}
                  {achievement.unlocked && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full border-2 border-card" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px]">
                <p className="font-semibold">{achievement.name}</p>
                <p className="text-xs text-muted-foreground">{achievement.description}</p>
                {!achievement.unlocked && achievement.progress !== undefined && (
                  <p className="text-xs text-primary mt-1">
                    Progress: {achievement.progress}/{achievement.maxProgress}
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
